/*
 * @providesModule WelcomeBackNavigator
 *
 */

import React from 'react';

import LoadingIndicator from 'LoadingIndicator';
import NavigationManager from 'NavigationManager';
import {buildStyleInterpolator, Navigator} from 'react-native-deprecated-custom-components';
import SKZConstants from 'SKZConstants';
import SKZType from 'SKZType';

import {View, StyleSheet, Modal, BackHandler, Platform} from 'react-native';

const SAFE_MODAL_CHANGE_TIME = 300;

let welcomeBackNavInstance: WelcomeBackNavigator | null = null;

export type ModalProps = {
  animationType: 'slide' | 'fade';
  transparent: boolean | string;
  modalBackHandler?: () => boolean | void;
};

interface WelcomeBackNavigatorProps {}

interface WelcomeBackNavigatorState {
  modalVisible: boolean;
  modalProps: ModalProps | Object;
  modalComponent: React.ReactNode | null;
}

export default class WelcomeBackNavigator extends React.Component<
  WelcomeBackNavigatorProps,
  WelcomeBackNavigatorState
> {
  welcomeBackNavigator: Navigator;

  constructor(props: WelcomeBackNavigatorProps) {
    super(props);
    welcomeBackNavInstance = this;

    this.state = {
      modalVisible: false,
      modalProps: {},
      modalComponent: null,
    };
  }

  componentDidMount() {
    if (Platform.OS == 'android') {
      this.backHandler = BackHandler.addEventListener('hardwareBackPress', (): boolean => {
        return true;
      });
    }
  }

  componentWillUnmount() {
    if (this.backHandler) {
      this.backHandler.remove();
    }
  }

  static pushComponent(
    component: React.ReactNode,
    animated: boolean = true,
    enableGestures: boolean = false,
    sceneTransition: Navigator.SceneConfigs = Navigator.SceneConfigs.FadeAndroid
  ) {
    this.pushComponentWithScene(component, animated, '', enableGestures, sceneTransition);
  }

  static pushComponentWithScene(
    component: React.ReactNode,
    animated: boolean = true,
    scene: string = '',
    enableGestures: boolean = false,
    sceneTransition: Navigator.SceneConfigs = Navigator.SceneConfigs.FadeAndroid
  ) {
    let transition = sceneTransition;
    transition.enableGesturesSkillz = enableGestures;

    if (!animated) {
      const noTransition = {
        opacity: {
          value: 1.0,
          type: 'constant',
        },
      };

      const NONE = Object.assign({}, Navigator.SceneConfigs.FadeAndroid, {
        gestures: null,
        defaultTransitionVelocity: SKZConstants.MS_PER_SEC,
        animationInterpolators: {
          into: buildStyleInterpolator(noTransition),
          out: buildStyleInterpolator(noTransition),
        },
      });
      transition = NONE;
    }

    welcomeBackNavInstance?.welcomeBackNavigator.push({
      scene: scene,
      configure: (): string => transition,
      render: (route: SKZType.Route, navigator: Navigator): React.ReactNode => component,
    });
  }

  static pop() {
    welcomeBackNavInstance?.welcomeBackNavigator.pop();
  }

  static popToTop() {
    welcomeBackNavInstance?.welcomeBackNavigator?.popToTop();
  }

  /*** Show Modal ***/

  static showModal(modalComponent: React.ReactNode, props: Object = {}, onRequestClose?: () => {}) {
    // LoadingIndicator uses an Animated.View, and this seems to interact poorly with
    // the React Modal component, if they're both changing state simultaneously.
    // The result is that the entire UI becomes untappable because one or the other isn't removed.
    // This forces the LoadingIndicator to stay shown while the modal is shown.
    LoadingIndicator.show();
    setTimeout(() => {
      const modalProps = {
        animationType: 'slide',
        transparent: 'true',
        ...props,
      };
      if (welcomeBackNavInstance) {
        welcomeBackNavInstance._onRequestClose =
          onRequestClose || props?.modalBackHandler || WelcomeBackNavigator.hideModal;
        welcomeBackNavInstance.setState({modalVisible: true, modalProps, modalComponent});
      }
    }, SAFE_MODAL_CHANGE_TIME);
  }

  static hideModal() {
    // LoadingIndicator uses an Animated.View, and this seems to interact poorly with
    // the React Modal component, if they're both changing state simultaneously.
    // The result is that the entire UI becomes untappable because one or the other isn't removed.
    // This forces the LoadingIndicator to stay shown until the modal is dismissed.
    setTimeout(() => {
      LoadingIndicator.hide();
    }, SAFE_MODAL_CHANGE_TIME);
    welcomeBackNavInstance?.setState({modalVisible: false, modalProps: {}, modalComponent: null});
  }

  static replaceComponent(
    component: ReactElement,
    scene: string,
    sceneTransition: Navigator.SceneConfigs = Navigator.SceneConfigs.HorizontalSwipeJump
  ) {
    welcomeBackNavInstance?.replaceComponent(component, scene, sceneTransition);
  }

  replaceComponent(
    component: ReactElement,
    scene: string,
    sceneTransition: Navigator.SceneConfigs = Navigator.SceneConfigs.HorizontalSwipeJump
  ) {
    welcomeBackNavInstance?.welcomeBackNavigator.replace({
      scene: scene,
      configure: (): string => sceneTransition,
      render: (route: SKZType.Route, navigator: Navigator): ReactElement => component,
    });
  }

  _renderScene(route: SKZType.Route, navigator: Navigator): React.ReactNode {
    return route.render(route, navigator);
  }

  _configureScene(route: SKZType.Route, routeStack: SKZType.Route[]): Navigator.SceneConfigs {
    let configuration = route ? route.configure() : null;

    if (configuration) {
      if (!configuration.enableGesturesSkillz) {
        configuration.gestures = false;
      }
    } else {
      configuration = {
        ...Navigator.SceneConfigs.HorizontalSwipeJump,
        gestures: false,
      };
    }
    return configuration;
  }

  _getHomeSceneConfiguration(): Navigator.SceneConfigs {
    return Navigator.SceneConfigs.FadeAndroid;
  }

  render(): React.ReactNode | null {
    const wbNavinitialScene = {
      scene: 'Blank Screen',
      render: (): React.ReactNode => <View style={StyleSheet.absoluteFill} />,
      configure: this._getHomeSceneConfiguration.bind(this),
    };

    return (
      <View style={StyleSheet.absoluteFill}>
        <Navigator
          renderScene={(route: SKZType.Route, navigator: Navigator): React.ReactNode | null => {
            return this._renderScene(route, navigator);
          }}
          ref={(nav: Navigator) => {
            this.welcomeBackNavigator = nav;
          }}
          configureScene={(route: SKZType.Route, routeStack: SKZType.Route[]): Object => {
            return this._configureScene(route, routeStack);
          }}
          sceneStyle={NavigationManager.AbsoluteFill}
          initialRoute={wbNavinitialScene}
        />
        <LoadingIndicator />

        <Modal
          {...this.state.modalProps}
          visible={this.state.modalVisible}
          onRequestClose={this._onRequestClose}
        >
          {this.state.modalComponent}
        </Modal>
      </View>
    );
  }
}
